import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// material
import { styled } from '@mui/material/styles';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  ButtonBase,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material';
import { KeyboardArrowDown, Check, Business, Refresh } from '@mui/icons-material';
// selectors & actions
import { selectWorkspaces } from '../../pages/login/selectors';
import { selectSession } from '../../session/selectors';
import { getWorkspaces } from '../../pages/login/actions';
import { setSession } from '../../session/actions';

// ----------------------------------------------------------------------

const WorkspaceButton = styled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'interactive',
})(({ theme, interactive }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: theme.spacing(1.5, 2),
  borderRadius: theme.shape.borderRadius,
  textAlign: 'left',
  cursor: interactive ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: interactive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
  },
}));

const WorkspaceLogo = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'showBackground',
})(({ theme, showBackground }) => ({
  width: 40,
  height: 40,
  marginRight: theme.spacing(1.5),
  backgroundColor: showBackground ? theme.palette.primary.main : 'transparent',
  fontSize: '1rem',
  fontWeight: 600,
}));

// ----------------------------------------------------------------------

export default function WorkspaceSelectorDropdown() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const workspaces = useSelector(selectWorkspaces());
  const session = useSelector(selectSession());

  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!workspaces);
  const [fetchError, setFetchError] = useState(false);
  const [imageError, setImageError] = useState(false);

  const open = Boolean(anchorEl);

  // Find current workspace from the list
  const currentWorkspace = workspaces?.find((ws) => ws.tenant === session?.tenant) || null;

  // Check if user has multiple workspaces
  const hasMultipleWorkspaces = workspaces?.length > 1;

  const fetchWorkspaces = () => {
    setInitialLoading(true);
    setFetchError(false);
    dispatch(getWorkspaces(() => {
      setFetchError(true);
      setInitialLoading(false);
    }));
  };

  useEffect(() => {
    if (!workspaces) {
      fetchWorkspaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (workspaces) {
      setInitialLoading(false);
      setFetchError(false);
    }
  }, [workspaces]);

  // Reset image error when workspace changes
  useEffect(() => {
    setImageError(false);
  }, [currentWorkspace?.id]);

  const handleClick = (event) => {
    if (hasMultipleWorkspaces) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleWorkspaceChange = (workspace) => {
    if (workspace.tenant === session?.tenant) {
      handleClose();
      return;
    }

    // With Cognito the access token is tenant-agnostic — every authenticated
    // request is scoped by the X-Tenant-Id header. Switching tenants is just
    // a Redux update; the api middleware picks up the new value on the next
    // request and the page reload re-fetches everything under the new tenant.
    setSwitching(true);
    dispatch(setSession({ ...session, tenant: workspace.tenant }));
    handleClose();
    window.location.reload();
  };

  const getWorkspaceInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Show skeleton while initial loading
  if (initialLoading) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1.5 }} />
          <Skeleton variant="text" width={120} height={24} />
        </Box>
      </Box>
    );
  }

  // Show error state with retry button
  if (fetchError && !currentWorkspace) {
    return (
      <Box sx={{ px: 1, py: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <WorkspaceLogo showBackground>
            <Business />
          </WorkspaceLogo>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary' }}
            >
              {t('common.api-error')}
            </Typography>
          </Box>
          <Tooltip title={t('common.retry')}>
            <IconButton size="small" onClick={fetchWorkspaces} sx={{ ml: 1 }}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 1, py: 2 }}>
      <WorkspaceButton onClick={handleClick} disabled={switching} interactive={hasMultipleWorkspaces} disableRipple={!hasMultipleWorkspaces}>
        {currentWorkspace?.logo && !imageError ? (
          <WorkspaceLogo
            src={currentWorkspace.logo}
            alt={currentWorkspace.name}
            imgProps={{
              onError: () => setImageError(true),
            }}
          />
        ) : (
          <WorkspaceLogo showBackground>
            {currentWorkspace?.name ? getWorkspaceInitials(currentWorkspace.name) : <Business />}
          </WorkspaceLogo>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {currentWorkspace?.name || ''}
          </Typography>
        </Box>
        {switching ? (
          <CircularProgress size={20} sx={{ color: 'text.secondary', ml: 1 }} />
        ) : (
          hasMultipleWorkspaces && (
            <KeyboardArrowDown
              sx={{
                color: 'text.secondary',
                ml: 1,
                transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
            />
          )
        )}
      </WorkspaceButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: 260,
            maxHeight: 400,
            mt: 1,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        {workspaces?.map((workspace) => (
          <MenuItem
            key={workspace.id}
            onClick={() => handleWorkspaceChange(workspace)}
            selected={workspace.tenant === session?.tenant}
            sx={{ py: 1.5 }}
          >
            <ListItemIcon>
              {workspace.logo ? (
                <Avatar src={workspace.logo} sx={{ width: 32, height: 32 }} />
              ) : (
                <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>
                  {getWorkspaceInitials(workspace.name)}
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary={workspace.name}
              primaryTypographyProps={{
                variant: 'body2',
                fontWeight: workspace.tenant === session?.tenant ? 600 : 400,
              }}
            />
            {workspace.tenant === session?.tenant && (
              <Check sx={{ ml: 1, color: 'primary.main', fontSize: 20 }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
